import React, { useState } from "react";
import { IconButton, Tooltip, Chip } from "@mui/material";
import {
  EXIT_REQUESTS,
  GET_MY_DELEGATION_REQUESTS,
  RESPOND_TO_DELEGATION_REQUESTS,
} from "@API/APIRoutes";
import ForwardToInboxIcon from "@mui/icons-material/ForwardToInbox";
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { ThemeProvider } from "@mui/system";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import MeatballsMenu from "@Components/MeatballsMenu/MeatballsMenu";
import * as DefaultRoutes from "@Routes/DefaultRoutes";
import GenericExitRequestListing from "../../GenericExitRequestListing/GenericExitRequestListing";
import FolderSpecialIcon from "@mui/icons-material/FolderSpecial";
import usePostItem from "../../../Hooks/usePostItem";
import dayjs from "dayjs";
import usePaginate from "@Hooks/usePaginate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DangerousIcon from "@mui/icons-material/Dangerous";
import DelegationReasonPopup from "@Components/DelegationReasonPopup/DelegationReasonPopup";
import DelegationActionPopup from "@Components/DelegationActionPopup/DelegationActionPopup";
import DelegateRequestDetails from "../../DelegateRequestDetails/DelegateRequestDetails";
import { stringCustomOperators, numberCustomOperators } from "@Utils/Validations/HelperFunctions/CustomFilters";
import {
  TagsContainer,
  TagItem,
} from "@Pages/Admin/ListingItems/ListingItems";

function RegulatoryMemberDelegationRequests() {
  const [showReasonPopup, setShowReasonPopup] = useState(false);
  const [showActionPopup, setShowActionPopup] = useState(false);
  const [showDetailsPage, setShowDetailsPage] = useState(false);
  const [selectedRequestForDetails, setSelectedRequestForDetails] =
    useState(null);
  const [actionType, setActionType] = useState(""); // "approve" or "decline"
  const [selectedRequest, setSelectedRequest] = useState(null);
  const { loading: actionLoading, postItem: respondToDelegationRequest } =
    usePostItem();
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const { loading: pageLoading, getPage } = usePaginate();
  const errorMessage = "Failed to fetch Requests !";

  // Define status colors for visual representation
  const getStatusColor = (status) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "REJECTED":
        return "error";
      case "ACCEPTED":
        return "success";
      default:
        return "default";
    }
  };

  // Format date to readable format
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return dayjs(dateStr).format("MM/DD/YYYY");
  };
  //********************* DETAILS PAGE *********************//
  const openDetailsPage = (request) => {
    setSelectedRequestForDetails(request);
    setShowDetailsPage(true);
  };

  const closeDetailsPage = () => {
    setShowDetailsPage(false);
    setSelectedRequestForDetails(null);
  };

  //********************* REASON POPUP *********************//
  const openReasonPopup = (request) => {
    setSelectedRequest(request);
    setShowReasonPopup(true);
  };

  const closeReasonPopup = () => {
    setShowReasonPopup(false);
    setSelectedRequest(null);
  };

  //********************* ACTION POPUP *********************//
  const openActionPopup = (request, action) => {
    setSelectedRequest(request);
    setActionType(action);
    setShowActionPopup(true);
  };

  const closeActionPopup = () => {
    setShowActionPopup(false);
    setSelectedRequest(null);
    setActionType("");
  };

  const handleActionConfirm = async (responseMessage = null) => {
    const requestData = {
      delegationRequestId: selectedRequest.id,
      accepted: actionType === "approve",
      responseMessage: responseMessage,
    };
    console.log("Request Data for Action:", requestData);
    await respondToDelegationRequest(
      RESPOND_TO_DELEGATION_REQUESTS,
      requestData,
      closeActionPopup,
      actionType === "approve"
        ? "Delegation request approved!"
        : "Delegation request declined!",
      "Failed to respond to delegation request!",
      true
    );

    // Update the rows state to reflect the changes
    setRows((prevRows) => {
      const updatedRows = prevRows.map((row) =>
        row.id === selectedRequest.id
          ? {
              ...row,
              status: actionType === "approve" ? "ACCEPTED" : "REJECTED",
            }
          : row
      );

      // Re-sort to maintain PENDING requests at the top
      return updatedRows.sort((a, b) => {
        if (a.status === "PENDING" && b.status !== "PENDING") return -1;
        if (b.status === "PENDING" && a.status !== "PENDING") return 1;
        return 0;
      });
    });
  };

  //********************* TABLE PROPS *********************//
  const meatballsMenuOptions = (request) => [
    {
      label: "Approve Delegate Request",
      icon: <CheckCircleIcon />,
      color: "green",
      action: () => openActionPopup(request, "approve"),
    },
    {
      label: "Decline Delegate Request",
      icon: <DangerousIcon />,
      color: "red",
      action: () => openActionPopup(request, "decline"),
    },
  ];

  // Fetch data from API
  const fetchPage = (queryOptions, filterModel) => {
    const filterItems = (filterModel?.items || []).map((item) => {
      const cleaned = { ...item };
      if (cleaned.value && typeof cleaned.value === "string") {
        cleaned.value = cleaned.value.trim();
        if (cleaned.field === "state") {
          cleaned.value = cleaned.value.replace(/\s+/g, "_");
        }
      }
      return cleaned;
    }).filter((item) => item.field && item.operator);

    getPage(
      GET_MY_DELEGATION_REQUESTS,
      errorMessage,
      queryOptions,
      filterItems
    ).then((data) => {
      if (data) {
        console.log("Delegation Requests Data:", data);

        // Sort the data to show PENDING requests first
        const sortedContent = [...data.content].sort((a, b) => {
          // If 'a' is PENDING and 'b' is not, 'a' should come first (return -1)
          if (a.status === "PENDING" && b.status !== "PENDING") return -1;
          // If 'b' is PENDING and 'a' is not, 'b' should come first (return 1)
          if (b.status === "PENDING" && a.status !== "PENDING") return 1;
          // If both have the same priority (both PENDING or both not PENDING), maintain original order
          return 0;
        });

        setRows(sortedContent);
        setRowCount(data.totalElements);
      }
    });
  };

  const columns = [
    {
      field: "id",
      headerName: "Request ID",
      flex: 0.8,
      minWidth: 90,
      type: "number",
      align: "left",
      filterOperators: numberCustomOperators,
    },
    {
      field: "ingredient",
      headerName: "Ingredient",
      flex: 1.2,
      minWidth: 160,
      type: "string",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div>
          <div style={{ fontWeight: 500 }}>
            {params.row.exitRequestIngredientName}
          </div>
          <div style={{ fontSize: "0.75rem", color: "gray" }}>
            CAS: {params.row.exitRequestIngredientCasNumber}
          </div>
        </div>
      ),
      filterOperators: stringCustomOperators,
    },
    {
      field: "ingredientName",
      headerName: "Ingredient Name",
      flex: 1.2,
      minWidth: 140,
      type: "string",
      valueGetter: (params) => params.row.exitRequestIngredientName,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "ingredientCasNumber",
      headerName: "CAS Number",
      flex: 1.2,
      minWidth: 120,
      type: "string",
      valueGetter: (params) => params.row.exitRequestIngredientCasNumber,
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "regionName",
      headerName: "Region",
      flex: 1.3,
      minWidth: 130,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
      renderCell: (params) =>
        params.row.regionName ? (
          <TagsContainer>
            <TagItem>{params.row.regionName}</TagItem>
          </TagsContainer>
        ) : (
          "-"
        ),
    },
    {
      field: "categoryName",
      headerName: "Category",
      flex: 1.3,
      minWidth: 140,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
      renderCell: (params) =>
        params.row.categoryName ? (
          <TagsContainer>
            <TagItem>{params.row.categoryName}</TagItem>
          </TagsContainer>
        ) : (
          "-"
        ),
    },
    {
      field: "requesterUsername",
      headerName: "Requester",
      flex: 1.3,
      minWidth: 140,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "proposedDelegateUsername",
      headerName: "Proposed Delegate",
      flex: 1.3,
      minWidth: 150,
      type: "string",
      filterOperators: stringCustomOperators,
      sortable: false,
    },
    {
      field: "createdAt",
      headerName: "Created At",
      flex: 1.2,
      minWidth: 120,
      type: "date",
      valueGetter: (params) =>
        params.row.createdAt ? new Date(params.row.createdAt) : null,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.2,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.row.status?.replace(/_/g, " ")}
          color={getStatusColor(params.row.status)}
          size="small"
          sx={{
            fontWeight: 600,
            textTransform: "capitalize",
          }}
        />
      ),
      type: "string",
      filterOperators: stringCustomOperators,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      disableReorder: true,
    },
  ];

  const initialColumnVisibilityModel = {
    createdAt: false,
    ingredientCasNumber: false,
    ingredientName: false,
  };

  const renderActions = (params, navigate, theme) => (
    <ThemeProvider theme={theme}>
      {params.row.status === "PENDING" && (
        <Tooltip title="Actions">
          <MeatballsMenu options={meatballsMenuOptions(params.row)} />
        </Tooltip>
      )}

      <Tooltip title="Preview Request Reason">
        <IconButton
          color="info"
          onClick={() => openReasonPopup(params.row)}
          size="small"
        >
          <LightbulbOutlinedIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Preview Full Request">
        <IconButton
          color="primary"
          onClick={() => openDetailsPage(params.row)}
          size="small"
        >
          <ForwardToInboxIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Preview Exit Request">
        <IconButton
          color="error"
          onClick={() =>
            navigate(
              DefaultRoutes.reqInfo.replace(":reqId", params.row.exitRequestId)
            )
          }
          size="small"
        >
          <ArrowCircleRightIcon />
        </IconButton>
      </Tooltip>
    </ThemeProvider>
  );

  return (
    <>
      {showDetailsPage ? (
        <DelegateRequestDetails
          requestData={selectedRequestForDetails}
          onBack={closeDetailsPage}
        />
      ) : (
        <>
          <GenericExitRequestListing
            title={"Delegation Requests"}
            PageIcon={<FolderSpecialIcon color="white" fontSize="large" />}
            iconColor="primary.main"
            apiRoute={EXIT_REQUESTS}
            columns={columns}
            renderActions={renderActions}
            errorMessage="Failed to Fetch My Exit Requests Trying Again!"
            extraProps={{ updateRows: setRows }}
            fetchPage={fetchPage}
            rows={rows}
            pageLoading={pageLoading}
            rowCount={rowCount}
            initialColumnVisibilityModel={initialColumnVisibilityModel}
          />

          <DelegationReasonPopup
            open={showReasonPopup}
            onClose={closeReasonPopup}
            reason={selectedRequest?.requestReason}
            requesterName={selectedRequest?.requesterUsername}
            proposedDelegate={selectedRequest?.proposedDelegateUsername}
          />

          <DelegationActionPopup
            open={showActionPopup}
            onClose={closeActionPopup}
            onConfirm={handleActionConfirm}
            actionType={actionType}
            loading={actionLoading}
          />
        </>
      )}
    </>
  );
}

export default RegulatoryMemberDelegationRequests;
